// src/merchant/MerchantProfile.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import {
  User, Phone, Mail, MapPin, Building2, CreditCard,
  Save, CheckCircle, Store, FileText, Truck,
} from 'lucide-react';
import toast from 'react-hot-toast';

const SECTION = ({ icon: Icon, title, color = 'rose', children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className={`flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-${color}-50/50`}>
      <div className={`w-9 h-9 bg-${color}-100 rounded-xl flex items-center justify-center`}>
        <Icon size={18} className={`text-${color}-500`} />
      </div>
      <h3 className="font-display font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-6 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-400 font-body mt-1">{hint}</p>}
  </div>
);

const inputClass = "w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-gray-800 text-sm bg-gray-50 focus:bg-white transition-all";

const EMPTY = {
  // Personal
  displayName: '', phone: '', email: '',
  // Shop
  shopName: '', shopDescription: '', gstNumber: '', panNumber: '',
  // Pickup address
  pickupName: '', pickupPhone: '', pickupAddress: '',
  pickupCity: '', pickupState: '', pickupPincode: '', pickupLandmark: '',
  // Bank
  accountHolderName: '', accountNumber: '', confirmAccountNumber: '',
  ifscCode: '', bankName: '', accountType: 'savings',
  // Razorpay
  razorpayLinkedAccountId: '',
  // Commission
  commissionRate: 10,
};

export default function MerchantProfile() {
  const { user } = useApp();
  const [form,    setForm]    = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const snap = await getDoc(doc(db, 'merchants', user.uid));
        if (snap.exists()) {
          setForm((f) => ({ ...f, ...snap.data() }));
        } else {
          // Pre-fill from auth
          setForm((f) => ({
            ...f,
            displayName: user.displayName || '',
            email:       user.email       || '',
          }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchProfile();
  }, [user]);

  const change = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    // Validate required fields
    if (!form.shopName.trim())           { toast.error('Shop name is required'); return; }
    if (!form.phone.trim())              { toast.error('Phone number is required'); return; }
    if (!form.pickupAddress.trim())      { toast.error('Pickup address is required'); return; }
    if (!form.pickupCity.trim())         { toast.error('Pickup city is required'); return; }
    if (!form.pickupPincode.trim())      { toast.error('Pickup pincode is required'); return; }
    if (form.accountNumber && form.accountNumber !== form.confirmAccountNumber) {
      toast.error('Account numbers do not match'); return;
    }
    if (form.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifscCode.toUpperCase())) {
      toast.error('Invalid IFSC code format'); return;
    }

    setSaving(true);
    try {
      const data = { ...form };
      delete data.confirmAccountNumber; // don't save confirm field
      await setDoc(doc(db, 'merchants', user.uid), {
        ...data,
        uid:       user.uid,
        email:     user.email,
        updatedAt: serverTimestamp(),
        status:    'active',
      }, { merge: true });
      toast.success('Profile saved successfully! ✅');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: 'personal',  label: 'Personal',  icon: User      },
    { id: 'shop',      label: 'Shop Info',  icon: Store     },
    { id: 'pickup',    label: 'Pickup',     icon: Truck     },
    { id: 'bank',      label: 'Bank',       icon: CreditCard },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-3xl space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">My Profile</h2>
          <p className="text-gray-400 font-body text-sm">Manage your shop, bank & pickup details</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 bg-green-50 text-green-600 px-4 py-2 rounded-full text-sm font-body font-semibold">
            <CheckCircle size={16} /> Saved!
          </div>
        )}
      </div>

      {/* Completion indicator */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-body font-semibold text-gray-700 text-sm">Profile Completion</p>
          <p className="font-body font-bold text-rose-500 text-sm">
            {Math.round([
              form.displayName, form.phone, form.shopName,
              form.pickupAddress, form.pickupCity, form.pickupPincode,
              form.accountNumber, form.ifscCode,
            ].filter(Boolean).length / 8 * 100)}%
          </p>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className="bg-rose-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${[
              form.displayName, form.phone, form.shopName,
              form.pickupAddress, form.pickupCity, form.pickupPincode,
              form.accountNumber, form.ifscCode,
            ].filter(Boolean).length / 8 * 100}%` }} />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {[
            { label: 'Personal Info',    done: !!(form.displayName && form.phone) },
            { label: 'Shop Info',        done: !!form.shopName                   },
            { label: 'Pickup Address',   done: !!(form.pickupAddress && form.pickupCity) },
            { label: 'Bank Details',     done: !!(form.accountNumber && form.ifscCode)   },
          ].map(({ label, done }) => (
            <span key={label} className={`text-xs px-3 py-1 rounded-full font-body font-medium ${
              done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
            }`}>
              {done ? '✓' : '○'} {label}
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-2xl p-1 shadow-sm border border-gray-100">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-body font-semibold transition-all ${
              activeTab === id
                ? 'bg-rose-500 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            <Icon size={14} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSave}>
        {/* Personal Info */}
        {activeTab === 'personal' && (
          <SECTION icon={User} title="Personal Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <input type="text" value={form.displayName} onChange={change('displayName')}
                  placeholder="Your full name" className={inputClass} />
              </Field>
              <Field label="Phone Number" required>
                <input type="tel" value={form.phone} onChange={change('phone')}
                  placeholder="10-digit mobile" className={inputClass} />
              </Field>
              <Field label="Email Address">
                <input type="email" value={form.email} disabled
                  className={`${inputClass} opacity-60 cursor-not-allowed`} />
              </Field>
            </div>
          </SECTION>
        )}

        {/* Shop Info */}
        {activeTab === 'shop' && (
          <SECTION icon={Store} title="Shop Information" color="purple">
            <Field label="Shop / Brand Name" required>
              <input type="text" value={form.shopName} onChange={change('shopName')}
                placeholder="e.g. Sneha's Handmade Gifts" className={inputClass} />
            </Field>
            <Field label="Shop Description" hint="Tell customers about your shop and what you make">
              <textarea value={form.shopDescription} onChange={change('shopDescription')} rows={3}
                placeholder="We create handmade gifts with love..."
                className={`${inputClass} resize-none`} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="GST Number" hint="Optional — required if turnover > ₹20L/year">
                <input type="text" value={form.gstNumber} onChange={change('gstNumber')}
                  placeholder="22AAAAA0000A1Z5" className={inputClass} />
              </Field>
              <Field label="PAN Number" hint="Required for payouts above ₹50,000">
                <input type="text" value={form.panNumber} onChange={change('panNumber')}
                  placeholder="ABCDE1234F" className={inputClass} />
              </Field>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <p className="text-amber-700 font-body text-xs font-semibold mb-1">⚠️ Important</p>
              <p className="text-amber-600 font-body text-xs">
                PAN is required for TDS compliance. If annual payouts exceed ₹50,000, TDS of 5% (without PAN) or 1% (with PAN) will be deducted as per Indian tax law.
              </p>
            </div>
          </SECTION>
        )}

        {/* Pickup Address */}
        {activeTab === 'pickup' && (
          <SECTION icon={Truck} title="Pickup Address" color="blue">
            <p className="text-gray-500 font-body text-sm bg-blue-50 border border-blue-100 rounded-2xl p-3">
              📦 This is the address where the courier will pick up your products to deliver to customers.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Contact Name" required>
                <input type="text" value={form.pickupName} onChange={change('pickupName')}
                  placeholder="Name for courier" className={inputClass} />
              </Field>
              <Field label="Contact Phone" required>
                <input type="tel" value={form.pickupPhone} onChange={change('pickupPhone')}
                  placeholder="10-digit mobile" className={inputClass} />
              </Field>
            </div>
            <Field label="Street Address" required hint="House/Flat no., Street, Area, Landmark">
              <textarea value={form.pickupAddress} onChange={change('pickupAddress')} rows={2}
                placeholder="Flat 4B, Rose Apartments, MG Road..."
                className={`${inputClass} resize-none`} />
            </Field>
            <Field label="Landmark" hint="Optional but helps courier find you faster">
              <input type="text" value={form.pickupLandmark} onChange={change('pickupLandmark')}
                placeholder="Near XYZ Mall" className={inputClass} />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="City" required>
                <input type="text" value={form.pickupCity} onChange={change('pickupCity')}
                  placeholder="City" className={inputClass} />
              </Field>
              <Field label="State" required>
                <input type="text" value={form.pickupState} onChange={change('pickupState')}
                  placeholder="State" className={inputClass} />
              </Field>
              <Field label="Pincode" required>
                <input type="text" value={form.pickupPincode} onChange={change('pickupPincode')}
                  placeholder="6-digit" maxLength={6} className={inputClass} />
              </Field>
            </div>
          </SECTION>
        )}

        {/* Bank Details */}
        {activeTab === 'bank' && (
          <SECTION icon={CreditCard} title="Bank Account Details" color="green">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-2">
              <p className="text-green-700 font-body text-xs font-semibold mb-1">🔒 Secure & Encrypted</p>
              <p className="text-green-600 font-body text-xs">
                Your bank details are stored securely. Payouts are processed by CoupleCart admin after order completion.
              </p>
            </div>
            <Field label="Account Holder Name" required>
              <input type="text" value={form.accountHolderName} onChange={change('accountHolderName')}
                placeholder="Exactly as in your bank account" className={inputClass} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Account Number" required>
                <input type="password" value={form.accountNumber} onChange={change('accountNumber')}
                  placeholder="Enter account number" className={inputClass} />
              </Field>
              <Field label="Confirm Account Number" required>
                <input type="text" value={form.confirmAccountNumber} onChange={change('confirmAccountNumber')}
                  placeholder="Re-enter account number" className={inputClass} />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="IFSC Code" required hint="11-character code on your cheque book">
                <input type="text" value={form.ifscCode} onChange={change('ifscCode')}
                  placeholder="SBIN0001234" maxLength={11}
                  className={`${inputClass} uppercase`} />
              </Field>
              <Field label="Bank Name" required>
                <input type="text" value={form.bankName} onChange={change('bankName')}
                  placeholder="State Bank of India" className={inputClass} />
              </Field>
            </div>
            <Field label="Account Type">
              <select value={form.accountType} onChange={change('accountType')} className={inputClass}>
                <option value="savings">Savings Account</option>
                <option value="current">Current Account</option>
              </select>
            </Field>

            {/* Razorpay Linked Account */}
            <div className="border-t border-gray-100 pt-4">
              <Field label="Razorpay Linked Account ID"
                hint="Provided by CoupleCart admin after bank verification. Used for automatic payment splits.">
                <input type="text" value={form.razorpayLinkedAccountId} onChange={change('razorpayLinkedAccountId')}
                  placeholder="route_XXXXXXXXXXXXXXXXXX" className={inputClass} />
              </Field>
            </div>
          </SECTION>
        )}

        {/* Save Button */}
        <div className="flex items-center gap-3 mt-5">
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-8 py-3.5 rounded-2xl font-body font-semibold transition-all shadow-[0_4px_15px_rgba(244,63,94,0.3)] disabled:opacity-60">
            {saving
              ? <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              : <Save size={18} />
            }
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          <div className="flex gap-2">
            {TABS.filter((t) => t.id !== activeTab).map((t) => (
              <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                className="px-4 py-3.5 rounded-2xl border border-gray-200 text-gray-500 hover:bg-gray-50 font-body text-sm transition-all">
                Next: {t.label}
              </button>
            )).slice(0, 1)}
          </div>
        </div>
      </form>
    </div>
  );
}
